/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState, useCallback } from "react";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  DragStartEvent,
} from "@dnd-kit/core";

type ExternalEvent = {
  id: string;
  title: string;
  durationMinutes: number;
  data?: any;
};

export type CalendarEvent = {
  id: string;
  title: string;
  start: Date;
  end: Date;
  data?: any;
};

type HoursRange = {
  start: number; // 0-23 (inclusive)
  end: number; // 1-24 (exclusive end)
};

type WeeklyCalendar2Props = {
  weekStart: Date; // start of week (00:00 of first day)
  events: CalendarEvent[];
  externalEvents: ExternalEvent[];

  // UI/behavior
  slotMinutes?: number; // granularity of time slots
  hoursRange?: HoursRange; // visible hours, default 0-24
  showExternalPanel?: boolean; // show built-in external panel
  disableOverlap?: boolean; // prevent overlaps (default true)
  headerDateFormat?: (date: Date) => string; // custom header renderer
  renderEvent?: (event: CalendarEvent) => React.ReactNode; // custom event renderer
  renderExternalEvent?: (ext: ExternalEvent) => React.ReactNode; // custom external event renderer

  // Callbacks
  onExternalEventDrop: (payload: {
    externalEvent: ExternalEvent;
    start: Date;
    end: Date;
    dayIndex: number;
  }) => void;

  onEventMove: (payload: {
    event: CalendarEvent;
    start: Date;
    end: Date;
    dayIndex: number;
  }) => void;

  onEventClick?: (event: CalendarEvent) => void;
};

type DragDataExternal = {
  type: "external";
  externalEvent: ExternalEvent;
};

type DragDataInternal = {
  type: "internal";
  eventId: string;
};

type DropCellData = {
  dayIndex: number;
  slotIndex: number;
  date: Date; // start time of that slot
};

const DAY_COUNT = 7;
const HOUR_HEIGHT = 48; // px per hour (affects slot height)
const BORDER_COLOR = "#e5e7eb"; // tailwind gray-200
const BG_GRID_ODD = "#fafafa";

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function addDays(date: Date, days: number) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function startOfDay(date: Date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

function setTime(date: Date, hours: number, minutes: number) {
  const d = new Date(date);
  d.setHours(hours, minutes, 0, 0);
  return d;
}

function addMinutes(date: Date, minutes: number) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() + minutes);
  return d;
}

function diffInMinutes(a: Date, b: Date) {
  return Math.round((a.getTime() - b.getTime()) / 60000);
}

function isSameDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function defaultHeaderFormat(date: Date) {
  const day = date.toLocaleDateString(undefined, { weekday: "short" });
  const md = date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  return `${day} ${md}`;
}

function toDayLabel(date: Date) {
  return date.toLocaleDateString(undefined, { weekday: "long" });
}

function timeLabel(h: number) {
  const d = new Date();
  d.setHours(h, 0, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric" });
}

function hasConflictOnDay(
  events: CalendarEvent[],
  dayDate: Date,
  candidateStart: Date,
  candidateEnd: Date,
  excludeId?: string
) {
  const sameDayEvents = events.filter(
    (e) => isSameDay(e.start, dayDate) && e.id !== excludeId
  );
  return sameDayEvents.some((e) => {
    // [start, end) style overlap
    return candidateStart < e.end && candidateEnd > e.start;
  });
}

// function eventDayIndex(weekStart: Date, ev: CalendarEvent) {
//   const sod = startOfDay(weekStart).getTime();
//   const evDay = startOfDay(ev.start).getTime();
//   const days = Math.round((evDay - sod) / (1000 * 60 * 60 * 24));
//   return days;
// }

// Draggable "external event" item
function ExternalEventItem({
  ext,
  children,
}: {
  ext: ExternalEvent;
  children?: React.ReactNode;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `external-${ext.id}`,
    data: { type: "external", externalEvent: ext } as DragDataExternal,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
        padding: 8,
        borderRadius: 6,
        border: `1px solid ${BORDER_COLOR}`,
        background: "#fff",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        marginBottom: 8,
        userSelect: "none",
      }}
      title={ext.title}
    >
      {children ?? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{ext.title}</div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            {Math.round(ext.durationMinutes)} min
          </div>
        </div>
      )}
    </div>
  );
}

// Droppable time-slot cell
function TimeSlotCell({
  id,
  data,
  height,
  isOddRow,
}: {
  id: string;
  data: DropCellData;
  height: number;
  isOddRow: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({
    id,
    data,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        borderBottom: `1px solid ${BORDER_COLOR}`,
        height,
        background: isOver
          ? "rgba(99,102,241,0.10)"
          : isOddRow
          ? BG_GRID_ODD
          : "#fff",
      }}
    />
  );
}

// Draggable internal event
function CalendarEventItem({
  event,
  renderEvent,
  onClick,
}: {
  event: CalendarEvent;
  renderEvent?: (e: CalendarEvent) => React.ReactNode;
  onClick?: (e: CalendarEvent) => void;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `event-${event.id}`,
    data: { type: "internal", eventId: event.id } as DragDataInternal,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => onClick?.(event)}
      style={{
        position: "absolute",
        left: 2,
        right: 2,
        borderRadius: 6,
        border: "1px solid #c7d2fe",
        background: "#eef2ff",
        color: "#3730a3",
        padding: 6,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
        cursor: "grab",
        opacity: isDragging ? 0.6 : 1,
        overflow: "hidden",
        pointerEvents: "auto",
      }}
      title={`${event.title}`}
    >
      {renderEvent ? (
        renderEvent(event)
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>{event.title}</div>
          <div style={{ fontSize: 11, color: "#4b5563" }}>
            {event.start.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            -{" "}
            {event.end.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WeeklyCalendar2({
  weekStart,
  events,
  externalEvents,
  onExternalEventDrop,
  onEventMove,
  onEventClick,
  renderEvent,
  renderExternalEvent,
  slotMinutes = 30,
  hoursRange = { start: 0, end: 24 },
  showExternalPanel = true,
  disableOverlap = true,
  headerDateFormat = defaultHeaderFormat,
}: WeeklyCalendar2Props) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 4 } })
  );

  const [activeDragType, setActiveDragType] = useState<
    "external" | "internal" | null
  >(null);
  const [activeDragPayload, setActiveDragPayload] = useState<
    ExternalEvent | CalendarEvent | null
  >(null);

  const days = useMemo(() => {
    return new Array(DAY_COUNT).fill(0).map((_, i) => addDays(weekStart, i));
  }, [weekStart]);

  const slotsPerHour = 60 / slotMinutes;
  const slotHeight = (HOUR_HEIGHT * slotMinutes) / 60;
  const totalSlots = (hoursRange.end - hoursRange.start) * slotsPerHour;

  const onDragStart = useCallback(
    (evt: DragStartEvent) => {
      const data = evt.active.data.current as
        | DragDataExternal
        | DragDataInternal
        | undefined;
      if (!data) return;
      if ("type" in data && data.type === "external") {
        setActiveDragType("external");
        setActiveDragPayload(data.externalEvent);
      } else if ("type" in data && data.type === "internal") {
        setActiveDragType("internal");
        const ev = events.find((e) => `event-${e.id}` === evt.active.id);
        if (ev) setActiveDragPayload(ev);
      }
    },
    [events]
  );

  const onDragEnd = useCallback(
    (evt: DragEndEvent) => {
      const { over, active } = evt;
      const data = active.data.current as
        | DragDataExternal
        | DragDataInternal
        | undefined;
      setActiveDragType(null);
      setActiveDragPayload(null);

      if (!over || !data) return;

      const drop = over.data.current as DropCellData | undefined;
      if (!drop) return;

      const dayIndex = drop.dayIndex;
      const dayDate = days[dayIndex];
      const slotStart = drop.date;

      if ("type" in data && data.type === "external") {
        const payload = data.externalEvent;
        const start = slotStart;
        const end = addMinutes(start, payload.durationMinutes);

        if (!disableOverlap || !hasConflictOnDay(events, dayDate, start, end)) {
          onExternalEventDrop({ externalEvent: payload, start, end, dayIndex });
        }
        return;
      }

      if ("type" in data && data.type === "internal") {
        const ev = events.find((e) => e.id === data.eventId);
        if (!ev) return;
        const duration = Math.max(15, diffInMinutes(ev.end, ev.start));
        const start = slotStart;
        const end = addMinutes(start, duration);

        if (
          !disableOverlap ||
          !hasConflictOnDay(events, dayDate, start, end, ev.id)
        ) {
          onEventMove({ event: ev, start, end, dayIndex });
        }
        return;
      }
    },
    [days, events, onExternalEventDrop, onEventMove, disableOverlap]
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: showExternalPanel ? "260px 1fr" : "1fr",
          gap: 12,
          width: "100%",
        }}
      >
        {showExternalPanel && (
          <div
            style={{
              border: `1px solid ${BORDER_COLOR}`,
              borderRadius: 8,
              padding: 12,
              background: "#fff",
              minHeight: 200,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 8 }}>
              External Events
            </div>
            <div>
              {externalEvents.length === 0 && (
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  No external events
                </div>
              )}
              {externalEvents.map((ext) => (
                <ExternalEventItem key={ext.id} ext={ext}>
                  {renderExternalEvent ? renderExternalEvent(ext) : undefined}
                </ExternalEventItem>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            border: `1px solid ${BORDER_COLOR}`,
            borderRadius: 8,
            overflow: "hidden",
            background: "#fff",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `80px repeat(${DAY_COUNT}, 1fr)`,
              borderBottom: `1px solid ${BORDER_COLOR}`,
              background: "#f9fafb",
            }}
          >
            <div
              style={{
                gridColumn: `1 / span ${DAY_COUNT + 1}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "6px 12px",
                gap: 8,
                background: "#ffffff",
                borderBottom: `1px solid ${BORDER_COLOR}`,
              }}
            >
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("weeklyCalendar2:changeWeek", {
                      detail: { direction: -1, current: weekStart },
                    })
                  )
                }
                style={{
                  fontSize: 11,
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: `1px solid ${BORDER_COLOR}`,
                  background: "#f3f4f6",
                  cursor: "pointer",
                }}
              >
                ‹ Prev
              </button>
              <div style={{ fontSize: 12, fontWeight: 600 }}>
                {weekStart.toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}{" "}
                –{" "}
                {addDays(weekStart, 6).toLocaleDateString(undefined, {
                  month: "short",
                  day: "numeric",
                })}
              </div>
              <button
                type="button"
                onClick={() =>
                  window.dispatchEvent(
                    new CustomEvent("weeklyCalendar2:changeWeek", {
                      detail: { direction: 1, current: weekStart },
                    })
                  )
                }
                style={{
                  fontSize: 11,
                  padding: "4px 8px",
                  borderRadius: 4,
                  border: `1px solid ${BORDER_COLOR}`,
                  background: "#f3f4f6",
                  cursor: "pointer",
                }}
              >
                Next ›
              </button>
            </div>

            {days.map((d, i) => (
              <div
                key={i}
                style={{
                  padding: "10px 8px",
                  fontSize: 12,
                  fontWeight: 700,
                  textAlign: "center",
                  borderLeft: `1px solid ${BORDER_COLOR}`,
                }}
              >
                <div>
                  {headerDateFormat
                    ? headerDateFormat(d)
                    : defaultHeaderFormat(d)}
                </div>
                <div
                  style={{ fontSize: 11, color: "#6b7280", fontWeight: 500 }}
                >
                  {toDayLabel(d)}
                </div>
              </div>
            ))}
          </div>

          {/* Body */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: `80px repeat(${DAY_COUNT}, 1fr)`,
            }}
          >
            {/* Time column */}
            <div>
              {new Array(hoursRange.end - hoursRange.start)
                .fill(0)
                .map((_, h) => {
                  const hour = hoursRange.start + h;
                  return (
                    <div
                      key={hour}
                      style={{
                        height: HOUR_HEIGHT,
                        borderBottom: `1px solid ${BORDER_COLOR}`,
                        fontSize: 11,
                        color: "#6b7280",
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "flex-end",
                        paddingRight: 6,
                        paddingTop: 2,
                        background: h % 2 ? BG_GRID_ODD : "#fff",
                        boxSizing: "border-box",
                      }}
                    >
                      {timeLabel(hour)}
                    </div>
                  );
                })}
            </div>

            {/* Day columns */}
            {days.map((dayDate, dayIndex) => {
              // Events that belong to this day
              const dayEvents = events
                .filter((e) => isSameDay(e.start, dayDate))
                .sort((a, b) => a.start.getTime() - b.start.getTime());

              // Absolute-positioned container for events
              const dayStart = setTime(
                startOfDay(dayDate),
                hoursRange.start,
                0
              );
              const dayEnd = setTime(
                startOfDay(dayDate),
                hoursRange.end - 1,
                59
              );

              return (
                <div
                  key={dayIndex}
                  style={{
                    position: "relative",
                    borderLeft: `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  {/* droppable grid */}
                  {new Array(totalSlots).fill(0).map((_, slotIndex) => {
                    const minutesFromStart = slotIndex * slotMinutes;
                    const slotTime = addMinutes(dayStart, minutesFromStart);
                    const id = `cell-${dayIndex}-${slotIndex}`;
                    const isOddRow =
                      Math.floor(slotIndex / slotsPerHour) % 2 === 1;
                    return (
                      <TimeSlotCell
                        key={id}
                        id={id}
                        data={{ dayIndex, slotIndex, date: slotTime }}
                        height={slotHeight}
                        isOddRow={isOddRow}
                      />
                    );
                  })}

                  {/* events absolute */}
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      pointerEvents: "none", // allow drag over grid; event items will re-enable their pointer events
                    }}
                  >
                    {dayEvents.map((ev) => {
                      // clamp visible area
                      const topMinutes = clamp(
                        diffInMinutes(ev.start, dayStart),
                        0,
                        diffInMinutes(dayEnd, dayStart) + 59
                      );
                      const durationMinutes = Math.max(
                        15,
                        diffInMinutes(ev.end, ev.start)
                      );
                      const visibleTop = (topMinutes / 60) * HOUR_HEIGHT;
                      const visibleHeight =
                        (durationMinutes / 60) * HOUR_HEIGHT;

                      return (
                        <div
                          key={ev.id}
                          style={{
                            position: "absolute",
                            top: visibleTop,
                            height: visibleHeight,
                            left: 0,
                            right: 0,
                            pointerEvents: "none",
                          }}
                        >
                          <CalendarEventItem
                            event={ev}
                            onClick={onEventClick}
                            renderEvent={renderEvent}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Drag Overlay */}
      <DragOverlay
        dropAnimation={{
          duration: 150,
          easing: "ease-out",
        }}
      >
        {activeDragType === "external" &&
        activeDragPayload &&
        "durationMinutes" in activeDragPayload ? (
          <div
            style={{
              padding: 8,
              borderRadius: 6,
              border: `1px solid ${BORDER_COLOR}`,
              background: "#fff",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {activeDragPayload.title}
          </div>
        ) : null}
        {activeDragType === "internal" &&
        activeDragPayload &&
        "start" in activeDragPayload ? (
          <div
            style={{
              padding: 8,
              borderRadius: 6,
              border: "1px solid #c7d2fe",
              background: "#eef2ff",
              color: "#3730a3",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {activeDragPayload.title}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/*
Usage notes:

- External events:
Pass an array of externalEvents and handle onExternalEventDrop to
add the new CalendarEvent into your state AND remove the external
event from the external list (to satisfy requirement #2).

Example handler:
  const handleExternalDrop = ({ externalEvent, start, end }) => {
    setEvents((prev) => [...prev, { id: nanoid(), title: externalEvent.title, start, end }]);
    setExternalEvents((prev) => prev.filter(e => e.id !== externalEvent.id));
  };

- Event move within calendar:
Implement onEventMove to update an event's start/end in your state.
The component prevents overlaps when disableOverlap=true.

- No overlapping:
When disableOverlap (default true), drops that would overlap on the same day are ignored.

- Customization:
- slotMinutes controls the granularity (e.g., 15, 30, 60).
- hoursRange controls visible hours (e.g., {start: 8, end: 20}).
- Use renderEvent to customize event card UI.
- Use headerDateFormat to customize header labels.

- This component owns the DndContext so that external items can be dropped in.
*/
